if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        // liftsets (19 Sep): an inbound ?variant=ID link (feed, Google Shopping) shows its readable path once loaded
        try {
          const params = new URLSearchParams(window.location.search);
          const vid = params.get('variant');
          if (vid && this.dataset.updateUrl !== 'false') {
            const pretty = this.prettyVariantUrl(this.dataset.url, vid);
            if (pretty) {
              params.delete('variant');
              const rest = params.toString();
              window.history.replaceState({}, '', pretty + (rest ? `?${rest}` : '') + window.location.hash);
            }
          }
        } catch (e) {}

        this.initializeProductSwapUtility();

        this.initFactsFilter();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);
          // liftsets (20 Sep, audit dims 131/142/196; Codex review): the 26-row comparison table is
          // rendered server side and is not part of the fragment this handler swaps in, so after a
          // client-side variant change its highlight and aria-current stayed on the previously selected
          // row. Move both to the row for the variant now selected.
          this.syncFactsTable(variant?.id);
          this.syncDocumentTitle(variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
            this.variantSelectors?.rejectPendingSelectPromise(error);
          });
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          // liftsets (21 Sep, accessibility session, dims 186, 167 and 187). Dawn replaces <variant-selects>
          // wholesale, which destroys the element the user is standing on, so keyboard focus fell to
          // <body>. Measured three times: on the select at +300 ms, on body by +1000 ms, still there at
          // +5000 ms. A keyboard or screen reader user who changed weight was returned to the top of the
          // document and had to traverse about 28 tab stops back to Add to cart. On a product whose whole
          // job is comparing 26 weights, that made the control unusable without a mouse.
          // Remember what was focused, and put focus back on the equivalent control after the swap.
          const active = document.activeElement;
          const wasInside = active && this.variantSelectors && this.variantSelectors.contains(active);
          const activeId = wasInside ? active.id : null;
          const activeName = wasInside ? active.getAttribute('name') : null;
          const selStart = wasInside && active.selectionStart != null ? active.selectionStart : null;

          // Restore focus in viewTransition's POST-PROCESS callback, which runs once the new node is in the
          // document. An earlier attempt used requestAnimationFrame and silently did nothing: the frame fired
          // before the swap, so it focused the old node, which viewTransition then renames and removes,
          // dropping focus to body exactly as before. viewTransition also appends a timestamp to every id on
          // the outgoing node, so the id captured beforehand belongs to the incoming one, which is what we
          // want.
          // Measured behaviour, 21 Sep: restoring inside the post-process callback is still too early.
          // Something after the swap, most likely variant-selects re-initialising on connect, blows focus
          // away again. Proven by experiment: four seconds after the change, focusing the select by id
          // works and STAYS. So the restore is attempted immediately and then re-asserted a few times
          // while focus is sitting on body, which is the only state we are correcting. Each attempt is a
          // no-op once focus is anywhere deliberate, so this cannot fight the user or a modal.
          const putFocusBack = () => {
            const settled = document.activeElement;
            if (settled && settled !== document.body && settled !== document.documentElement) return true;
            const scope = this.variantSelectors || this;
            let target = null;
            if (activeId) target = scope.querySelector(`#${CSS.escape(activeId)}`) || this.querySelector(`#${CSS.escape(activeId)}`);
            if (!target && activeName) target = scope.querySelector(`[name="${CSS.escape(activeName)}"]`);
            if (!target) return false;
            target.focus({ preventScroll: true });
            if (selStart != null && target.setSelectionRange) {
              try { target.setSelectionRange(selStart, selStart); } catch (e) { /* not a text input */ }
            }
            return document.activeElement === target;
          };

          const restoreFocus = () => {
            if (!wasInside) return;
            putFocusBack();
            [120, 350, 700, 1200].forEach((ms) => setTimeout(putFocusBack, ms));
          };

          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks,
            [() => restoreFocus()]
          );
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.syncFactsTable(variant?.id);
          this.syncDocumentTitle(variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          // liftsets (21 Sep, audit dim 172). The Klarna Pay in 3 line under the payment marks sits outside
          // the fragment Dawn swaps, so it kept the previous variant's instalment: switching 17.5 kg to
          // 10 kg moved the price to £41.49 while the line still read £24.83. That is a regulated credit
          // promotion showing a figure that is wrong for 25 of the 26 weights on the normal path through
          // this page. Copy the server's own re-rendered line rather than recomputing it here, so the
          // rounding and the £35 to £1,000 eligibility window stay in one place, in the Liquid.
          updateSourceFromDestination('Bnpl');
          updateSourceFromDestination('Bnpl-buy');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
          // liftsets (22 Sep). The shipped-weight line under the delivery promise is per variant: the hex
          // pairs run from 2 kg to 120 kg the pair across one page, and that figure is the only thing that
          // decides the delivery band for Northern Ireland. A variant
          // change is a section render, not a page load, so take the server's own re-rendered sentence the
          // same way the price and the Klarna line are taken, rather than recomputing kilograms here.
          updateSourceFromDestination('Ls-Weight');

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      // liftsets (19 Sep): readable variant URLs. When the variant has a custom.url_slug the address
      // bar and the share link get /products/<handle>/<slug> (served by Shopify as the product page;
      // sections/main-product.liquid selects the variant from request.path). No slug: Dawn's ?variant=.
      prettyVariantUrl(url, variantId) {
        // liftsets (21 Sep, URGENT REVERT): disabled. Writing /products/<handle>/<slug> into the address bar
        // produced a URL that a visitor could copy and share, and that URL does not work: Shopify answers 302
        // for a real browser and normalises the extra segment away, landing the recipient on the parent
        // product at the default weight. Measured in Chrome with a clean profile on 21 September. Returning
        // null here makes Dawn keep its own ?variant= URL, which is correct and shareable. The server-side
        // slug resolution stays in place, so a readable URL that does reach the theme still works.
        // Do not re-enable without re-testing in a real browser; curl does not reproduce the redirect.
        return null;
        // eslint-disable-next-line no-unreachable
        const el = this.querySelector('[data-ls-variant-slugs]');
        if (!el || !variantId) return null;
        if (!this._lsSlugs) {
          this._lsSlugs = {};
          (el.textContent || '').split(',').forEach((pair) => {
            const i = pair.indexOf(':');
            if (i > 0) this._lsSlugs[pair.slice(0, i).trim()] = pair.slice(i + 1).trim();
          });
          this._lsProductPath = el.dataset.productPath || url;
        }
        const slug = this._lsSlugs[String(variantId)];
        return slug ? `${this._lsProductPath}/${slug}` : null;
      }

      syncDocumentTitle(variantId) {
        // liftsets (21 Sep, audit dim 59, owner's question). A variant change inside one product is a
        // section render, not a full page load, so Dawn never touches the head: the tab said 17.5 kg while
        // the URL had already become /60kg. The server titles each variant URL with that variant's Google
        // Merchant Center title, so the client only has to keep up.
        // Codex's review notes applied: read explicit data-seo-title and data-seo-desc attributes rather
        // than parsing presentation text, and update Open Graph too, so a share after a variant change
        // carries the same strings as a direct visit rather than the previous variant's.
        if (!variantId) return;
        const row = this.querySelector(`tr[data-variant-id="${variantId}"]`);
        if (!row) return;
        const title = row.dataset.seoTitle;
        const desc = row.dataset.seoDesc;
        if (title) {
          const current = document.title;
          const dash = current.lastIndexOf(' \u2013 ');
          const suffix = dash > -1 ? current.slice(dash) : '';
          document.title = suffix ? title + suffix : title;
          const ogTitle = document.querySelector('meta[property="og:title"]');
          if (ogTitle) ogTitle.setAttribute('content', title);
          const twTitle = document.querySelector('meta[name="twitter:title"]');
          if (twTitle) twTitle.setAttribute('content', title);
        }
        // Codex's note: the head is not what a buyer reads. Keep the visible line under the h1 in step too.
        const line = this.querySelector('[data-ls-selected-weight]');
        if (line && row.dataset.opt) {
          // Same wording and order as the server renders it, read from explicit attributes so the two
          // cannot drift apart.
          const esc = (v) => String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
          const kg = row.dataset.pairKg;
          // liftsets (22 Sep): "the pair" used to be hard-coded here, so a SET variant was announced as a
          // pair after every client-side variant change even once the server had stopped saying it. The
          // word is now resolved server-side and published as data-ls-unit on the line itself, so there is
          // one source of truth. Falls back to "in total", which is true of every weight on the site.
          const unit = line.dataset.lsUnit || 'in total';
          line.innerHTML = 'Showing <strong>' + esc(row.dataset.opt) + '</strong>' + (kg ? ' \u00b7 ' + esc(kg) + ' kg ' + esc(unit) : '');
        }
        if (desc) {
          const meta = document.querySelector('meta[name="description"]');
          if (meta) meta.setAttribute('content', desc);
          const ogDesc = document.querySelector('meta[property="og:description"]');
          if (ogDesc) ogDesc.setAttribute('content', desc);
          const twDesc = document.querySelector('meta[name="twitter:description"]');
          if (twDesc) twDesc.setAttribute('content', desc);
        }
      }

      // liftsets (25 Sep, merge + filter). Progressive enhancement over the merged specification table:
      // the <select> below does not exist in the HTML, so with JavaScript off every weight is on the page,
      // exactly as the server rendered it. The only thing this code ever does is add or remove a class on
      // a row -- no row is removed from the DOM, no data is fetched, nothing is re-rendered.
      //
      // The PDP default differs from the collection page's on purpose. The collection has no selected
      // weight, so it opens on "All". This page always has one variant selected, so opening on that
      // variant is the honest default: the reader asked for 10 kg, the spec table answers for 10 kg.
      // "All weights" stays one keystroke away and is never the empty state.
      initFactsFilter() {
        this.querySelectorAll('table.ls-facts-table[data-dims="true"]').forEach((table) => {
          if (table.dataset.lsFilterReady === 'true') return;
          const rows = Array.from(table.querySelectorAll('tbody tr[data-weight]'));
          if (rows.length < 2) return;

          const scroll = table.closest('[data-ls-facts-scroll]') || table.parentElement;
          if (!scroll || !scroll.parentElement) return;

          const weights = [];
          rows.forEach((row) => {
            const w = row.dataset.weight;
            if (w && weights.indexOf(w) === -1) weights.push(w);
          });
          if (weights.length < 2) return;

          const optionName = table.dataset.filterLabel || 'weight';
          const id = `${table.id || 'FactsTable'}-filter`;

          const wrap = document.createElement('div');
          wrap.className = 'ls-facts-filter';

          const label = document.createElement('label');
          label.className = 'ls-facts-filter__label';
          label.setAttribute('for', id);
          label.textContent = `Show ${optionName.toLowerCase()}:`;

          const select = document.createElement('select');
          select.className = 'ls-facts-filter__select';
          select.id = id;

          const all = document.createElement('option');
          all.value = '';
          all.textContent = `All ${weights.length} weights`;
          select.appendChild(all);
          weights.forEach((w) => {
            const opt = document.createElement('option');
            opt.value = w;
            opt.textContent = w;
            select.appendChild(opt);
          });

          const status = document.createElement('p');
          status.className = 'ls-facts-filter__status';
          status.setAttribute('role', 'status');
          status.setAttribute('aria-live', 'polite');

          wrap.appendChild(label);
          wrap.appendChild(select);
          wrap.appendChild(status);
          scroll.parentElement.insertBefore(wrap, scroll);

          table.lsFilter = { select, status, rows };
          table.dataset.lsFilterReady = 'true';
          select.addEventListener('change', () => this.applyFactsFilter(table, select.value));

          // Open on the variant the page is already showing, not on "All".
          const current = table.querySelector('tbody tr[aria-current="true"][data-weight]');
          this.applyFactsFilter(table, (current && current.dataset.weight) || '');
        });
      }

      applyFactsFilter(table, value) {
        const state = table.lsFilter;
        if (!state) return;
        // A value with no matching row would blank the table, which is worse than showing everything.
        const match = value ? state.rows.some((row) => row.dataset.weight === value) : true;
        const active = match ? value : '';
        state.rows.forEach((row) => {
          row.classList.toggle('ls-is-filtered', active !== '' && row.dataset.weight !== active);
        });
        if (state.select.value !== active) state.select.value = active;
        state.status.textContent = active
          ? `Showing ${active}. ${state.rows.length} weights are published in this table.`
          : `Showing all ${state.rows.length} published weights.`;
      }

      syncFactsTable(variantId) {
        // liftsets (20 Sep, audit dims 131/142/196). Reviewer notes applied in full.
        // Scope: the comparison table is rendered inside this <product-info> element, so query from `this`
        // only. Codex rejected an earlier version that fell back to a document-wide query, which could let
        // one product-info rewrite another product's table. There is no fallback now by design.
        // Null or unmatched variant clears every row, because Dawn's unavailable-combination branch returns
        // before any DOM replacement and a stale aria-current would tell a screen reader the wrong thing
        // (Grok's note). After a successful change the fragment is re-fetched with ?variant= and the table
        // is re-rendered server side, so this keeps the interim state honest rather than being the only fix.
        const tables = this.querySelectorAll('table.ls-facts-table');
        tables.forEach((table) => {
          const rows = table.querySelectorAll('tr[data-variant-id]');
          if (!rows.length) return;
          let matched = false;
          rows.forEach((row) => {
            const isCurrent = variantId != null && String(row.dataset.variantId) === String(variantId);
            if (isCurrent) matched = true;
            row.classList.toggle('is-selected', isCurrent);
            if (isCurrent) {
              row.setAttribute('aria-current', 'true');
            } else {
              row.removeAttribute('aria-current');
            }
          });
          if (!matched) {
            rows.forEach((row) => {
              row.classList.remove('is-selected');
              row.removeAttribute('aria-current');
            });
          }

          // liftsets (25 Sep): follow the variant while the reader is looking at one weight. If they have
          // deliberately chosen "All weights", leave them on "All" -- a variant change must not silently
          // undo an explicit choice. An unmatched variant falls back to "All" rather than an empty table.
          if (table.lsFilter && table.lsFilter.select.value !== '') {
            const current = table.querySelector('tbody tr[aria-current="true"][data-weight]');
            this.applyFactsFilter(table, (current && current.dataset.weight) || '');
          }
        });
      }

      updateURL(url, variantId) {
        const pretty = this.prettyVariantUrl(url, variantId);
        const target = pretty || `${url}${variantId ? `?variant=${variantId}` : ''}`;
        this.querySelector('share-button')?.updateUrl(`${window.shopUrl}${target}`);

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', target);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        // liftsets (22 Sep): 'Ls-Weight' joins the list so an unavailable option combination does not leave
        // the previous variant's shipped weight standing next to a hidden price. The standing delivery note
        // after it is outside this span and stays, because it is true whatever is selected.
        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules', 'Ls-Weight']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // liftsets BUILD-liv: the variant-only gallery renders a different thumbnail set per variant
        // (that weight's spec sheet), so the thumbnail list and the counter come from the new HTML too.
        // Dawn only syncs the main slides above; without this the previous variant's sheet thumbnail stays.
        const gallery = this.querySelector('media-gallery');
        const thumbsSource = gallery?.querySelector('.thumbnail-slider');
        const thumbsDestination = html.querySelector('media-gallery .thumbnail-slider');
        if (thumbsSource && thumbsDestination) {
          if (thumbsSource.innerHTML !== thumbsDestination.innerHTML) thumbsSource.replaceWith(thumbsDestination);
        } else if (thumbsSource && !thumbsDestination) {
          thumbsSource.remove(); // one image only: Dawn renders no thumbnail strip
        } else if (!thumbsSource && thumbsDestination && gallery) {
          const viewer = gallery.querySelector('slider-component');
          if (viewer) viewer.after(thumbsDestination); else gallery.append(thumbsDestination);
        }
        const moreSource = gallery?.querySelector('.ls-thumbs-more');
        const moreDestination = html.querySelector('media-gallery .ls-thumbs-more');
        if (moreSource && !moreDestination) moreSource.remove();
        const counterSource = this.querySelector('media-gallery .slider-counter--total');
        const counterDestination = html.querySelector('media-gallery .slider-counter--total');
        if (counterSource && counterDestination) counterSource.textContent = counterDestination.textContent;
        this.querySelector('media-gallery slider-component')?.resetPages?.();

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
